import React from 'react';
import { Maximize2, Minimize2, Pencil, Settings, Film } from 'lucide-react';
import { LatencyBadge, Tooltip, type PeerLatency } from '@trinetra/shared';
import LiveStreamPlayer from '../LiveStreamPlayer';
import DebugOverlay from '../DebugOverlay';
import type { CameraState } from '../../services/node-connection-manager';
import type { CameraStatsSnapshot } from '../../services/cloud-stats-collector';

interface Props {
  nodeId: string;
  nodeName: string;
  cameraId: string;
  cameraName: string;
  stream: MediaStream | null;
  state: CameraState;
  isFullscreen: boolean;
  fillContainer?: boolean;
  pingInfo?: PeerLatency | null;
  onDoubleClick: () => void;
  onRetry: () => void;
  onStalled?: () => void;
  onPlaying?: () => void;
  /** Camera actions — each omitted when the viewer lacks the permission for it. */
  onEdit?: () => void;
  onConfig?: () => void;
  onPlayback?: () => void;
  /** Browser-side WebRTC stats for this camera; non-null only while debug mode is on. */
  debugStats?: CameraStatsSnapshot | null;
}

export default function StreamTile({
  nodeId,
  nodeName,
  cameraId,
  cameraName,
  stream,
  state,
  isFullscreen,
  fillContainer = false,
  pingInfo,
  onDoubleClick,
  onRetry,
  onStalled,
  onPlaying,
  onEdit,
  onConfig,
  onPlayback,
  debugStats,
}: Props) {
  // In fullscreen the parent wrapper is promoted to the browser fullscreen element (via the
  // Fullscreen API), so the tile just needs to fill it — NOT position:fixed itself, which is
  // fragile when an ancestor establishes a containing block (leaves the tile invisible).
  const outerClass = `relative group cursor-pointer ${
    isFullscreen || fillContainer ? 'w-full h-full' : 'aspect-video overflow-hidden'
  }`;
  const playerClass = isFullscreen || fillContainer ? 'w-full h-full' : 'w-full h-full object-contain';

  return (
    <div className={outerClass} onDoubleClick={onDoubleClick}>
      <LiveStreamPlayer
        stream={stream}
        connectionState={state}
        label={cameraName}
        subLabel={nodeName}
        fillContainer={fillContainer}
        onStalled={onStalled}
        onPlaying={onPlaying}
        onRetry={onRetry}
        className={playerClass}
      >
        {/* Camera actions — the same set the Cameras page offers on hover. They stay
            hidden until the tile is hovered so a dense grid isn't peppered with icons,
            and they're suppressed in fullscreen (navigating out of a fullscreen tile
            is never what the click meant). Each stops propagation so it can't be read
            as the double-click that toggles fullscreen. */}
        <div className="flex items-center gap-0.5" onDoubleClick={(e) => e.stopPropagation()}>
          {!isFullscreen && (onEdit || onConfig || onPlayback) && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              {onEdit && (
                <Tooltip label="Edit camera details" side="top">
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                    className="text-white/60 hover:text-white transition-colors p-0.5"
                    aria-label="Edit camera details"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </Tooltip>
              )}
              {onConfig && (
                <Tooltip label="ONVIF configuration — PTZ, imaging & stream profiles" side="top">
                  <button
                    onClick={(e) => { e.stopPropagation(); onConfig(); }}
                    className="text-white/60 hover:text-white transition-colors p-0.5"
                    aria-label="ONVIF configuration"
                  >
                    <Settings className="h-3.5 w-3.5" />
                  </button>
                </Tooltip>
              )}
              {onPlayback && (
                <Tooltip label="Recording playback — review this camera's past footage" side="top">
                  <button
                    onClick={(e) => { e.stopPropagation(); onPlayback(); }}
                    className="text-white/60 hover:text-white transition-colors p-0.5"
                    aria-label="Recording playback"
                  >
                    <Film className="h-3.5 w-3.5" />
                  </button>
                </Tooltip>
              )}
            </div>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); onDoubleClick(); }}
            className="text-white/60 hover:text-white transition-colors p-0.5"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      </LiveStreamPlayer>

      {/* Per-camera WebRTC stats — the same readout the Cameras page shows in debug
          mode, drawn over the frame because a grid tile has no room below it. */}
      {debugStats && <DebugOverlay stats={debugStats} />}

      {/* Latency — top right, clear of the hover actions in the player's bottom bar. Same
          component the Cameras page uses, so the readout can't drift between the two.
          In fullscreen it shifts inward to clear the exit button MultiSiteView pins at
          top-4 right-4. */}
      {pingInfo && <LatencyBadge latency={pingInfo} className={isFullscreen ? 'top-4 right-16' : 'top-1.5 right-1.5'} />}
    </div>
  );
}
